import { useQuery } from '@tanstack/react-query'
import { Fingerprint, ShieldAlert } from 'lucide-react'
import { useState } from 'react'

import { auditTrailQueryOptions } from '@/api/audit-api'
import type { AuditEntry } from '@/api/types'
import { ErrorSurface } from '@/components/error-surface'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/auth/use-auth'
import { formatDateTime } from '@/lib/datetime'
import { PERMISSIONS } from '@/lib/permissions'

export interface AuditTrailDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Entity name as the server records it: `Customer`, `LoanCycle`, `TellerSession`. */
  entityType: string
  entityId: string
  /** What the operator calls this record — an account number or a customer name. */
  subject?: string
}

const OUTCOME_TONES = {
  Succeeded: 'success',
  Failed: 'danger',
  Denied: 'warning',
} as const

/**
 * The audit trail for one record.
 *
 * The trail is append-only on the server and sealed into a hash chain, so nothing shown here can
 * be edited or removed — this panel is a window onto evidence, not a log viewer. Denied attempts
 * appear alongside successful ones, because an attempt that was refused is exactly what an
 * examiner asks to see.
 *
 * The query fires only when the panel opens: reading an audit trail is itself an audited action,
 * and prefetching would write an access record for a page the user merely visited.
 */
export function AuditTrailDrawer({
  open,
  onOpenChange,
  entityType,
  entityId,
  subject,
}: AuditTrailDrawerProps) {
  const { hasPermission } = useAuth()
  const [page, setPage] = useState(1)
  const permitted = hasPermission(PERMISSIONS.ops.auditView)

  const query = useQuery({
    ...auditTrailQueryOptions({ entityType, entityId, page }),
    enabled: open && permitted,
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Fingerprint className="size-4 text-brand-text" aria-hidden />
            Audit trail
          </SheetTitle>
          <SheetDescription>
            {subject ? `${subject} · ` : ''}
            {entityType} <span className="font-mono">{entityId}</span>
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="space-y-3">
          {!permitted ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <ShieldAlert className="size-6 text-warning-text" aria-hidden />
              <p className="text-sm font-medium text-heading">Audit access is restricted</p>
              <p className="max-w-sm text-xs text-muted">
                Viewing the trail needs the {PERMISSIONS.ops.auditView} permission.
              </p>
            </div>
          ) : query.isPending ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-20 w-full" />
              ))}
            </div>
          ) : query.isError ? (
            <ErrorSurface error={query.error} onRetry={() => void query.refetch()} />
          ) : query.data.items.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted">
              No audit entries recorded against this record yet.
            </p>
          ) : (
            <ol className="space-y-2">
              {query.data.items.map((entry) => (
                <AuditEntryCard key={entry.id} entry={entry} />
              ))}
            </ol>
          )}
        </SheetBody>

        {permitted && query.data && query.data.totalPages > 1 ? (
          <SheetFooter>
            <span className="mr-auto text-xs text-muted">
              Page {query.data.page} of {query.data.totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= query.data.totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

function AuditEntryCard({ entry }: { entry: AuditEntry }) {
  const hasStateDiff = Boolean(entry.beforeJson ?? entry.afterJson)

  return (
    <li className="rounded-card border border-border-subtle bg-surface-muted p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-xs font-medium text-heading">{entry.actionCode}</span>
        <Badge tone={OUTCOME_TONES[entry.outcome]}>{entry.outcome}</Badge>
      </div>

      <p className="mt-1 text-xs text-body">
        {entry.actorUsername}
        {entry.approverUsername ? ` · approved by ${entry.approverUsername}` : ''} ·{' '}
        <span className="numeric tabular-nums">{formatDateTime(entry.occurredAtUtc)}</span>
      </p>

      <p className="mt-0.5 text-2xs text-muted">
        {entry.source}
        {entry.sourceIpAddress ? ` · ${entry.sourceIpAddress}` : ''}
        {entry.deviceId ? ` · device ${entry.deviceId}` : ''}
      </p>

      {entry.failureReason ? (
        <p className="mt-1 text-xs text-danger-text">{entry.failureReason}</p>
      ) : null}

      {hasStateDiff ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-2xs font-medium text-brand-text">
            Before and after
          </summary>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <JsonPanel label="Before" json={entry.beforeJson} />
            <JsonPanel label="After" json={entry.afterJson} />
          </div>
        </details>
      ) : null}

      <p className="mt-2 font-mono text-2xs text-muted">ref {entry.correlationId}</p>
    </li>
  )
}

function JsonPanel({ label, json }: { label: string; json?: string }) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</p>
      <pre className="mt-1 max-h-56 overflow-auto rounded-control border border-border-subtle bg-surface p-2 font-mono text-2xs text-body">
        {json ? prettyJson(json) : '—'}
      </pre>
    </div>
  )
}

/** Server-supplied JSON is displayed as-is when it will not parse — never silently blanked. */
function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}
